import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ProgressSummary from '@/components/progress/ProgressSummary';
import CourseProgress from '@/components/progress/CourseProgress';
import { apiClient } from '@/lib/api';
import { Enrollment } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';
// interface Enrollment {
//   id: number;
//   course: {
//     id: number;
//     title: string;
//     description: string;
//   };
//   progress: number;
//   enrolled_at: string;
// }

export default function ProgressPage() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getEnrollments();
      
      if (response.success && response.data) {
        setEnrollments(response.data?.results);
        // Select the first enrollment by default
        if (response.data?.results.length > 0 && !selectedEnrollment) {
          setSelectedEnrollment(response.data?.results[0].id);
        }
      } else {
        setError(response.error || 'Failed to load enrollments');
      }
    } catch (err) {
      setError('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Please log in to view your progress</h1>
          <p className="text-gray-600">You need to be authenticated to access this page.</p>
        </div>
      </div>
    );
  }

  if (user.role !== 'student') {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Access Restricted</h1>
          <p className="text-gray-600">Progress tracking is only available for students.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Error Loading Progress</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={loadEnrollments}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-gray-900">My Learning Progress</h1>
          <p className="text-gray-600 mt-2">Track your progress across all enrolled courses</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Progress Summary */}
          <div className="lg:col-span-3">
            <ProgressSummary />
          </div>

          {/* Course Progress */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Course Progress</h2>
              </div>
              
              {enrollments.length === 0 ? (
                <EmptyState
                  icon="📈"
                  title="No enrollments yet."
                  description="You haven't enrolled in any courses yet."
                  action={
                    <Link
                      to="/courses"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Browse Courses
                    </Link>
                  }
                  hintsTitle="Get started:"
                  hints={[
                    { icon: '📚', label: 'Browse available courses' },
                    { icon: '✅', label: 'Enroll to start tracking progress' },
                  ]}
                />
              ) : (
                <div>
                  {/* Course Selection */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <label htmlFor="course-select" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Course
                    </label>
                    <select
                      id="course-select"
                      value={selectedEnrollment || ''}
                      onChange={(e) => setSelectedEnrollment(Number(e.target.value))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {enrollments && enrollments?.map((enrollment) => (
                        <option key={enrollment.id} value={enrollment.id}>
                          {enrollment.course.title} ({enrollment.progress}% complete)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Course Progress Details */}
                  {selectedEnrollment && (
                    <div className="p-6">
                      <CourseProgress enrollmentId={selectedEnrollment} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

